import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Badge,
    Box,
    Button,
    Input,
    Spinner,
    VStack,
} from '@chakra-ui/react';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbBuilding } from 'react-icons/tb';
import LocationSelector from '../../../components/LocationSelector';
import SessionContext from '../../../contexts/SessionContext';
import {
    SettingsActionBar,
    SettingsCard,
    SettingsRow,
} from '../../../components/settings/primitives';
import { useProfileDraft } from './useProfileDraft';

const normalizeLocations = (myLocations?: Array<string> | 'all'): Array<string> | 'all' => {
    if (myLocations === 'all') return 'all';
    return Array.isArray(myLocations) ? myLocations : [];
};

const locationsEqual = (
    left?: Array<string> | 'all',
    right?: Array<string> | 'all',
): boolean => {
    const a = normalizeLocations(left);
    const b = normalizeLocations(right);
    if (a === 'all' || b === 'all') return a === b;
    return a.length === b.length && a.every((id) => b.includes(id));
};

const ProfileSection = () => {
    const session = useContext(SessionContext);
    const navigate = useNavigate();
    const { profile, isLoading, isError, error, isDirty, isSaving, setField, save, discard } =
        useProfileDraft();

    // Only users who can see every location choose a subset for themselves.
    const showMyLocations = session?.isAuthenticated === true && session.locationId === 'all';

    if (isLoading || !profile) {
        return (
            <Box display="flex" justifyContent="center" py={12}>
                <Spinner size="xl" />
            </Box>
        );
    }

    if (isError) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>Error loading profile!</AlertTitle>
                <AlertDescription>{error?.message || 'An unknown error occurred.'}</AlertDescription>
            </Alert>
        );
    }

    const inputProps = {
        size: 'sm' as const,
        w: '400px',
        maxW: '100%',
        borderColor: 'gray.200',
    };

    return (
        <>
            <VStack spacing={6} align="stretch" pb={16}>
                <SettingsCard
                    title="Personal information"
                    headerAction={
                        profile.admin ? (
                            <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                                Admin
                            </Badge>
                        ) : undefined
                    }
                >
                    <SettingsRow
                        label="First name"
                        control={
                            <Input
                                {...inputProps}
                                value={profile.firstName || ''}
                                onChange={(e) => setField('firstName', e.target.value)}
                            />
                        }
                    />
                    <SettingsRow
                        label="Last name"
                        control={
                            <Input
                                {...inputProps}
                                value={profile.lastName || ''}
                                onChange={(e) => setField('lastName', e.target.value)}
                            />
                        }
                    />
                    <SettingsRow
                        label="Username"
                        control={
                            <Input
                                {...inputProps}
                                value={profile.username || ''}
                                onChange={(e) => setField('username', e.target.value)}
                            />
                        }
                    />
                    <SettingsRow
                        label="Email"
                        description="Used for sign in and for any emails you have turned on."
                        control={
                            <Input
                                {...inputProps}
                                type="email"
                                value={profile.email || ''}
                                onChange={(e) => setField('email', e.target.value)}
                            />
                        }
                    />
                    <SettingsRow
                        label="Location"
                        description="Set by an administrator."
                        control={
                            <Input
                                {...inputProps}
                                value={session?.locationName || 'No location'}
                                isReadOnly
                                bg="gray.50"
                                color="gray.600"
                                cursor="not-allowed"
                            />
                        }
                    />
                </SettingsCard>

                {showMyLocations && (
                    <SettingsCard title="My locations">
                        <SettingsRow
                            label="Locations"
                            description="Choose which locations you want associated with your profile. Leave as all to follow your assigned locations."
                            isStacked
                        >
                            <LocationSelector
                                selectedLocations={normalizeLocations(profile.myLocations)}
                                onLocationChange={(locationIds) =>
                                    setField('myLocations', locationIds, locationsEqual)
                                }
                                width="100%"
                                hideOtherEmployeeLocations
                                allowEmptySelection
                                icon={TbBuilding}
                            />
                        </SettingsRow>
                    </SettingsCard>
                )}

                <SettingsCard title="Security">
                    <SettingsRow
                        label="Password"
                        description="Change your account password."
                        control={
                            <Button
                                size="sm"
                                variant="outlineGray"
                                onClick={() => navigate('/forgot-password')}
                            >
                                Change password
                            </Button>
                        }
                    />
                </SettingsCard>
            </VStack>

            <SettingsActionBar
                isDirty={isDirty}
                isSaving={isSaving}
                onSave={save}
                onDiscard={discard}
            />
        </>
    );
};

export default ProfileSection;
